/**
 * F09 v2 — query-string builder for the admin verification endpoints.
 *
 * Lives outside `api/verification.ts` on purpose: the vitest alias rewrites
 * `api/verification` to its fixture, so keeping the URL builder here lets the
 * tests exercise the real query shape the client sends.
 *
 * Array values are sent as REPEATED params (`?status=pending&status=more_info_needed`),
 * never as `status[]=` (carry-forward ruling — the API accepts repeated params or
 * a CSV). `undefined` / `null` / `''` values and empty arrays are omitted.
 */
export type VerificationQueryValue =
  | string
  | number
  | boolean
  | readonly string[]
  | null
  | undefined;

export function buildVerificationQuery(params: Record<string, VerificationQueryValue>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value as readonly string[]) {
        if (item !== '') usp.append(key, item);
      }
      continue;
    }
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

/** Parse a CSV URL param (`?status=a,b`) into a de-duplicated list of allowed values. */
export function parseCsvParam<T extends string>(
  raw: string | null,
  allowed: readonly T[],
): readonly T[] {
  if (!raw) return [];
  const out: T[] = [];
  for (const part of raw.split(',')) {
    const v = part.trim();
    if ((allowed as readonly string[]).includes(v) && !out.includes(v as T)) out.push(v as T);
  }
  return out;
}
